import {
  ReactElement,
  forwardRef,
  Ref,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import Loading from './Loading';

export interface IProps<K, T> {
  keyName?: K;
  items: Array<T | number | string>;
  isEnd: boolean;
  isFetching?: boolean;
  isSlow?: boolean;
  overScanRowCount?: number;
  itemClassName?: string;
  requestGetNextList?: () => void | boolean; // 실제 request 를 하지 않았다면 명시적으로 false 를 반환해야한다
  children: (index: number) => ReactElement | null;
  getLoadingIndicator?: () => ReactElement;
  noRowsRenderer?: () => ReactElement | null;
}

// TODO: forwardRef를 사용하면 T에 없는 keyName을 입력해도 에러가 나지 않는다.
function List<K extends string, T extends {[key in K]: any}>(
  {
    keyName,
    items,
    isEnd,
    isFetching,
    isSlow,
    overScanRowCount = 10,
    itemClassName,
    requestGetNextList = () => {},
    children,
    getLoadingIndicator = getDefaultLoading,
    noRowsRenderer = () => null,
  }: IProps<K, T>,
  ref: Ref<IListRef>
) {
  const scrollParentRef = useRef<HTMLElement | Window>();
  const listRef = useRef<HTMLUListElement>(null);

  useImperativeHandle(ref, () => ({
    scrollTo,
  }));
  const scrollTo = (index: number) => {
    if (!listRef.current) {
      return;
    }

    if (0 <= index && index < items.length) {
      const rowEl = listRef.current.children.item(index) as HTMLElement;
      const parentEl = scrollParentRef.current;
      if (rowEl && parentEl) {
        if ('location' in parentEl) {
          window.scrollTo(0, rowEl.offsetTop);
        } else {
          if (parentEl.scrollTo) {
            parentEl.scrollTo(0, rowEl.offsetTop - parentEl.offsetTop);
          }
        }
      }
    }
  };

  useEffect(() => {
    const listEl = listRef.current;
    if (listEl) {
      let el: HTMLElement | null = listEl.parentElement;
      while (el !== null) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (overflowY && SCROLL_KEYS.includes(overflowY)) {
          scrollParentRef.current = el;
          return;
        }
        el = el.parentElement;
      }
      scrollParentRef.current = window;
    }
  }, []);

  const observerRef = useRef<IntersectionObserver>();
  const requestRowCountRef = useRef<number>(0);
  const requestGetNextListRef = useRef(requestGetNextList);
  requestGetNextListRef.current = requestGetNextList;
  const onIntersection = useCallback(
    (rows: IntersectionObserverEntry[]) => {
      const appearRows = rows.filter(row => row.isIntersecting);
      const appearMaxIndex =
        appearRows.length > 0
          ? Number((appearRows.pop()!.target as HTMLElement).dataset.index)
          : -1;

      if (
        !isEnd &&
        appearMaxIndex + overScanRowCount >= items.length - 1 &&
        items.length > requestRowCountRef.current
      ) {
        if (requestGetNextListRef.current() !== false) {
          requestRowCountRef.current = items.length;
        }
      }
    },
    [isEnd, overScanRowCount, items]
  );

  const prevCountRef = useRef<number>(0);
  useEffect(() => {
    if (items.length < prevCountRef.current) {
      requestRowCountRef.current = 0;
    }
    prevCountRef.current = items.length;

    observerRef.current = new IntersectionObserver(onIntersection, {
      root:
        scrollParentRef.current === window
          ? null
          : (scrollParentRef.current as Element),
    });

    if (listRef.current) {
      for (const row of listRef.current.children) {
        (row as HTMLElement).dataset.index && observerRef.current.observe(row);
      }
    }

    return () => observerRef.current && observerRef.current.disconnect();
  }, [items, onIntersection]);

  const rowsEl = useMemo(() => {
    return items.map((item, index) => (
      <li
        key={
          !keyName || typeof item === 'string' || typeof item === 'number'
            ? index
            : item[keyName]
        }
        className={cn('w-full', itemClassName)}
        data-index={index}
      >
        {children(index)}
      </li>
    ));
  }, [items, itemClassName, children, keyName]);

  return (
    <ul className="flex flex-wrap" ref={listRef}>
      {!!rowsEl.length && (
        <>
          {rowsEl}
          {!isEnd && getLoadingIndicator()}
        </>
      )}
      {!rowsEl.length && isFetching && isSlow && getLoadingIndicator()}
      {!rowsEl.length && !isFetching && noRowsRenderer()}
    </ul>
  );
}

export default forwardRef(List);

const SCROLL_KEYS = ['auto', 'scroll', 'overlay'];

const getDefaultLoading = () => {
  return (
    <div className="w-full flex-none">
      <Loading />
    </div>
  );
};

export interface IListRef {
  scrollTo: (index: number) => void;
}
